/**
 * PhoneInput — country-code dropdown + digit-only national number field.
 *
 * Currently supports UAE only (+971). The list is structured to be easily extended
 * later (Saudi Arabia, Bahrain, etc.) without touching consumers.
 *
 * Value model: the consumer sees a single E.164 string (e.g. "+971501234567" or "").
 * Internally we split it into { dialCode, national }.
 *
 * Props:
 *   value         — current E.164 string
 *   onChange(e164)— called with the normalized string ("" if empty)
 *   onValidChange — optional, called with a boolean every time validity flips
 *   disabled, dataTestidPrefix, language
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export const PHONE_COUNTRIES = [
  {
    code: 'AE',
    dialCode: '+971',
    nameAr: 'الإمارات',
    nameEn: 'UAE',
    flag: '🇦🇪',
    nationalLength: 9,         // digits AFTER the dial code (e.g. 501234567)
    mobilePrefixes: ['50', '51', '52', '54', '55', '56', '58'],
    // landline first digits allowed
    landlinePrefixes: ['2', '3', '4', '6', '7', '9'],
  },
  // Future countries go here — keep the same shape
];

function splitE164(value, country) {
  if (!value) return { national: '' };
  // Strip everything except digits and +
  const cleaned = String(value).replace(/[^\d+]/g, '');
  if (cleaned.startsWith(country.dialCode)) {
    return { national: cleaned.slice(country.dialCode.length) };
  }
  // Tolerate values that came without dial code
  return { national: cleaned.replace(/^\+/, '') };
}

export function validateNationalNumber(national, country) {
  if (!national) return { valid: false, reason: 'empty' };
  if (!/^\d+$/.test(national)) return { valid: false, reason: 'non_digits' };
  if (national.length !== country.nationalLength) {
    return { valid: false, reason: 'length' };
  }
  // First digit / 2-digit prefix must match a known UAE mobile or landline prefix
  const okMobile = country.mobilePrefixes.some((p) => national.startsWith(p));
  const okLandline = country.landlinePrefixes.some((p) => national.startsWith(p));
  if (!okMobile && !okLandline) {
    return { valid: false, reason: 'prefix' };
  }
  return { valid: true };
}

export default function PhoneInput({
  value = '',
  onChange,
  onValidChange,
  disabled = false,
  dataTestidPrefix = 'phone',
  language = 'ar',
  required = true,
  placeholder,
}) {
  const country = PHONE_COUNTRIES[0]; // UAE for now
  const [open, setOpen] = useState(false);
  const initial = useMemo(() => splitE164(value, country), [value, country]);
  const [national, setNational] = useState(initial.national);

  // Keep local state in sync if parent resets value externally
  useEffect(() => {
    const next = splitE164(value, country);
    if (next.national !== national) setNational(next.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const v = validateNationalNumber(national, country);
  const showError = !v.valid && (national.length > 0 || required);

  // Notify parent of validity changes
  useEffect(() => {
    onValidChange?.(v.valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.valid]);

  const handleNationalChange = (raw) => {
    // Allow only digits, max length of country.nationalLength
    const digits = raw.replace(/\D/g, '').slice(0, country.nationalLength);
    setNational(digits);
    const e164 = digits ? `${country.dialCode}${digits}` : '';
    onChange?.(e164);
  };

  const errorText = !v.valid && national.length > 0 ? (
    v.reason === 'length'
      ? (language === 'ar'
          ? `الرقم يجب أن يكون ${country.nationalLength} أرقام`
          : `Must be ${country.nationalLength} digits`)
      : v.reason === 'prefix'
        ? (language === 'ar' ? 'بداية الرقم غير صالحة لدولة الإمارات' : 'Prefix not valid for UAE')
        : (language === 'ar' ? 'أرقام فقط' : 'Digits only')
  ) : null;

  return (
    <div className="relative" data-testid={`${dataTestidPrefix}-input-wrapper`}>
      <div
        className={`flex items-stretch rounded-md border bg-white/5 overflow-hidden ${
          showError && errorText ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#d4af37]/50'
        }`}
        dir="ltr"
      >
        {/* Country picker */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled || PHONE_COUNTRIES.length === 1}
          data-testid={`${dataTestidPrefix}-country-trigger`}
          className="flex items-center gap-1.5 px-3 bg-white/5 hover:bg-white/10 disabled:opacity-100 disabled:cursor-default border-e border-white/10 text-white text-sm shrink-0"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="font-mono">{country.dialCode}</span>
          {PHONE_COUNTRIES.length > 1 && (
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>

        {/* National number */}
        <input
          value={national}
          onChange={(e) => handleNationalChange(e.target.value)}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder || (country.nationalLength === 9 ? '5XXXXXXXX' : '')}
          data-testid={`${dataTestidPrefix}-national-input`}
          className="flex-1 bg-transparent px-3 py-2 text-white text-sm outline-none font-mono placeholder:text-[#71717A]"
        />

        {/* OK indicator */}
        {national.length > 0 && v.valid && (
          <span className="px-2 flex items-center text-emerald-400" aria-hidden>
            ✓
          </span>
        )}
      </div>

      {/* Country list — appears only if more than one country */}
      {open && PHONE_COUNTRIES.length > 1 && (
        <div
          className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-[#1a1a2e] border border-white/10 rounded-md shadow-lg"
          data-testid={`${dataTestidPrefix}-country-list`}
        >
          {PHONE_COUNTRIES.map((c) => (
            <button
              type="button"
              key={c.code}
              onClick={() => { setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/5"
            >
              <span>{c.flag}</span>
              <span>{language === 'ar' ? c.nameAr : c.nameEn}</span>
              <span className="ms-auto text-[#A1A1AA] font-mono">{c.dialCode}</span>
            </button>
          ))}
        </div>
      )}

      {errorText && (
        <p className="text-xs text-red-400 mt-1" data-testid={`${dataTestidPrefix}-error`}>
          {errorText}
        </p>
      )}
    </div>
  );
}
